import { FetchResult } from 'apollo-boost';

import {
    addInternalGroups,
    copyAdGroupsMutation,
    createSmartAdGroup,
    createTextAdGroup,
    remoderateAdsCalloutsMutation,
    updateAdGroup,
    updateCpmAdGroup,
    updateInternalGroup
} from '../../graphql/mutations/grid/groups';
import { updateAdGroupMinusKeywordsMutation } from '../../graphql/mutations/grid/minus-phrase';
import {
    addInternalAdGroups,
    CopyAdGroupsMutation,
    CreateSmartAdGroup,
    CreateTextAdGroup,
    GdAddInternalAdGroupsInput,
    GdAddSmartAdGroupItemInput,
    GdAddTextAdGroupItemInput,
    GdCopyAdGroupsInput,
    GdRemoderateAdsCalloutsInput,
    GdUpdateAdGroupMinusKeywordsInput,
    GdUpdateCpmAdGroupInput,
    GdUpdateInternalAdGroupsInput,
    GdUpdatePerformanceAdGroupInput,
    GdUpdateTextAdGroupInput,
    RemoderateAdsCallouts,
    UpdateAdGroup,
    updateAdGroupMinusKeywords,
    UpdateCpmAdGroup,
    updateInternalAdGroups
} from '../../typings/graphql-grid';
import { Omit } from '../../typings/utils';
import { BaseMutationExecutor } from '../BaseMutationExecutor/BaseMutationExecutor';

export class GroupsMutationExecutor extends BaseMutationExecutor {
    public async updateGroups(
        textInput: GdUpdateTextAdGroupInput,
        smartInput: GdUpdatePerformanceAdGroupInput,
        login: string
    ): Promise<FetchResult<UpdateAdGroup & { notSynced?: boolean }>> {
        return this.client.awaitMutate<UpdateAdGroup>({
            mutation: updateAdGroup,
            variables: {
                textInput,
                smartInput,
                login
            },
            login
        });
    }

    public updateGroupsMinusPhraseBind(
        login: string,
        input: GdUpdateAdGroupMinusKeywordsInput
    ): Promise<FetchResult<updateAdGroupMinusKeywords>> {
        return this.client.mutate<updateAdGroupMinusKeywords>({
            mutation: updateAdGroupMinusKeywordsMutation,
            variables: {
                input,
                login
            }
        });
    }

    public async createTextGroup(
        textInput: GdAddTextAdGroupItemInput,
        login: string
    ): Promise<FetchResult<CreateTextAdGroup>> {
        return this.client.awaitMutate<CreateTextAdGroup>({
            mutation: createTextAdGroup,
            variables: {
                textInput,
                login
            },
            login
        });
    }

    public async updateInternalGroups(
        internalInput: GdUpdateInternalAdGroupsInput,
        login: string
    ): Promise<FetchResult<updateInternalAdGroups>> {
        return this.client.awaitMutate<updateInternalAdGroups>({
            mutation: updateInternalGroup,
            variables: {
                internalInput
            },
            login
        });
    }

    public async createSmartGroup(
        smartInput: GdAddSmartAdGroupItemInput,
        login: string
    ): Promise<FetchResult<CreateSmartAdGroup>> {
        return this.client
            .awaitMutate<CreateSmartAdGroup>({
                mutation: createSmartAdGroup,
                variables: {
                    smartInput,
                    login
                },
                login
            })
            .then(data => {
                // TODO убрать в DIRECT-98677
                if (data.data === undefined) {
                    return data;
                } else {
                    const { addedAdGroupItems } = data.data.addSmartAdGroups;

                    return {
                        ...data,
                        data: {
                            ...data.data,
                            addSmartAdGroups: {
                                ...data.data.addSmartAdGroups,
                                addedAdGroupItems: addedAdGroupItems.map(
                                    item => {
                                        return item === null
                                            ? item
                                            : {
                                                  ...item,
                                                  adGroupId: String(
                                                      item.adGroupId
                                                  )
                                              };
                                    }
                                )
                            }
                        }
                    };
                }
            });
    }

    public async sendRemoderateAdsCallouts(
        input: Omit<GdRemoderateAdsCalloutsInput, 'moderateAccept'>,
        login: string
    ): Promise<RemoderateAdsCallouts> {
        return await this.client
            .awaitMutate<RemoderateAdsCallouts>({
                mutation: remoderateAdsCalloutsMutation,
                variables: {
                    input: {
                        ...input,
                        moderateAccept: false
                    },
                    login
                },
                login
            })
            .then(
                (result: FetchResult<RemoderateAdsCallouts>) =>
                    result.data as RemoderateAdsCallouts
            );
    }

    public async sendRemoderateAcceptAdsCallouts(
        input: Omit<GdRemoderateAdsCalloutsInput, 'moderateAccept'>,
        login: string
    ): Promise<RemoderateAdsCallouts> {
        return await this.client
            .awaitMutate<RemoderateAdsCallouts>({
                mutation: remoderateAdsCalloutsMutation,
                variables: {
                    input: {
                        ...input,
                        moderateAccept: true
                    },
                    login
                },
                login
            })
            .then(
                (result: FetchResult<RemoderateAdsCallouts>) =>
                    result.data as RemoderateAdsCallouts
            );
    }

    public async duplicateGroups(
        input: GdCopyAdGroupsInput,
        login: string
    ): Promise<CopyAdGroupsMutation> {
        return await this.client
            .awaitMutate<CopyAdGroupsMutation>({
                mutation: copyAdGroupsMutation,
                variables: {
                    input,
                    login
                },
                login
            })
            .then(
                (result: FetchResult<CopyAdGroupsMutation>) =>
                    result.data as CopyAdGroupsMutation
            );
    }

    public async addInternalGroups(
        internalInput: GdAddInternalAdGroupsInput,
        login: string
    ): Promise<FetchResult<addInternalAdGroups>> {
        return this.client.awaitMutate<addInternalAdGroups>({
            mutation: addInternalGroups,
            variables: {
                internalInput
            },
            login
        });
    }

    public async updateCpmGroups(
        cpmInput: GdUpdateCpmAdGroupInput,
        login: string
    ): Promise<FetchResult<UpdateCpmAdGroup & { notSynced?: boolean }>> {
        return this.client.awaitMutate<UpdateCpmAdGroup>({
            mutation: updateCpmAdGroup,
            variables: {
                cpmInput,
                login
            },
            login
        });
    }
}
